import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import {
  Box, Toolbar, Typography, Grid, Button, CircularProgress, Tabs, Tab, Paper, Stack, Card, CardContent
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ShareButton from '../Share/ShareButton';
import { formatDateWithSuffix } from '../utils/formatDate';
import { formatTimeToAMPM } from '../utils/formatTime';
import { formatDuration } from '../utils/formatDuration';

import Header1 from '../Header/Header1';
import Platform from './Platform';
import Footer2 from '../Footer/Footer2';
import NeonSpeedometer from '../Post/NeonSpeedometer';

const API_URL = process.env.REACT_APP_API_URL;

function MyBookings({ searchResults, onLogout }) {

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState('Upcoming');
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const { userId } = useAuth();
  const { role, roles } = useAuth();
  const userRoles = Array.isArray(roles) && roles.length > 0 ? roles : [role];
  const { loggedIn } = useAuth();

  const allowedRoles = ['anthra-sales-executive', 'admin', 'expert'];

  const limit = 10;
  const totalPages = Math.ceil(count / limit);
  const navigate = useNavigate();

  useEffect(() => {
    let timer;

    if (loading) {
      setLoadingSeconds(0); // reset timer
      timer = setInterval(() => {
        setLoadingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timer);
      setLoadingSeconds(0); // optional reset after done
    }

    return () => clearInterval(timer); // cleanup on unmount or loading = false
  }, [loading]);

  // const fetchBookings = async (pageNumber, statusValue) => {
  //   setLoading(true);
  //   try {
  //     const response = await fetch(
  //       `${API_URL}/list_my_appointments/?userId=${userId}&role=${role}&page=${pageNumber}&limit=${limit}&status=${statusValue}`
  //     );
  //     if (!response.ok) throw new Error('Failed to fetch bookings');
  //     const data = await response.json();
  //     setCourses(data.results || []);
  //     setCount(data.count || 0);
  //   } catch (err) {
  //     setError(err.message || 'Unknown error');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   if (userId) {
  //     fetchBookings(page, status);
  //   }
  // }, [userId, page, status]);

  // Wherever you derive `role` from your auth context/store:

// ✅ Prefer roles[] array over single role string
// If user has ["user", "expert"], treat them as expert
const resolvedRole = Array.isArray(userRoles) && userRoles.includes("expert")
  ? "expert"
  : (role || "user");

// Then use resolvedRole in the fetch call:
const fetchBookings = async (pageNumber, statusValue) => {
  setLoading(true);
  try {
    const response = await fetch(
      `${API_URL}/list_my_appointments/?userId=${userId}&role=${resolvedRole}&page=${pageNumber}&limit=${limit}&status=${statusValue}`
    );
    if (!response.ok) throw new Error('Failed to fetch bookings');
    const data = await response.json();
    setCourses(data.results || []);
    setCount(data.count || 0);
  } catch (err) {
    setError(err.message || 'Unknown error');
  } finally {
    setLoading(false);  
  }
};

useEffect(() => {
  if (userId) {
    fetchBookings(page, status);
  }
}, [userId, page, status, resolvedRole]); 

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const formatStartTime = (timeStr) => {
    if (!timeStr) return '';
    const time = new Date(`1970-01-01T${timeStr}`);
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  if (!userId) return <div>Loading...</div>;

  if (loading && page === 1) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(3px)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={60}
            thickness={6}
            sx={{
              color: 'transparent',
              '& .MuiCircularProgress-circle': {
                stroke: 'url(#better-gradient)',
              },
            }}
          />

          {/* Gradient definition */}
          <svg width="0" height="0">
            <defs>
              <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f30b96ff" />
                <stop offset="50%" stopColor="#221ce0ff" />
                <stop offset="100%" stopColor="#0eacebff" />
              </linearGradient>
            </defs>
          </svg>

          <Typography
            variant="h6"
            sx={{
              mt: 2,
              color: '#fff',
              textShadow: '0 1px 3px rgba(0,0,0,0.7)',
            }}
          >
            Loading Sessions... ({loadingSeconds} sec)
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'error.main' }}>
        <ErrorOutlineIcon sx={{ fontSize: 40 }} />
        <Typography variant="h6">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>

      <Box
        sx={{
          position: 'fixed', // or 'relative' / 'sticky' based on your layout
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1300, // higher than MUI drawers (default is 1200)
        }}
      >
        <Header1 onLogout={onLogout} />
      </Box>
      <Box>
        <Platform userId={userId} onLogout={onLogout} />
      </Box>

      {loggedIn && (
        <Box position="static" color="primary" sx={{ mt: 7, backgroundColor: '#000000' }}>
          <Toolbar>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 2 }}>

              {/* Single Button */}
              <Button
                variant={isActive('/platform/explore') ? 'contained' : 'outlined'}
                color={isActive('/platform/explore') ? 'secondary' : 'inherit'}
                onClick={() => navigate('/platform/explore')}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  bgcolor: isActive('/platform/explore') ? 'secondary.main' : 'transparent',
                  color: isActive('/platform/explore') ? 'white' : 'inherit',
                  border: '2px solid',
                  borderColor: isActive('/platform/explore') ? 'primary.main' : 'inherit',
                  '&:hover': {
                    bgcolor: isActive('/platform/explore') ? 'secondary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Explore
              </Button>

              {/* Multiple Button */}
              {allowedRoles.some(r => userRoles.includes(r)) && (
                <Button
                  variant={isActive('/platform/my-hosts') ? 'contained' : 'outlined'}
                  color={isActive('/platform/my-hosts') ? 'secondary' : 'inherit'}
                  onClick={() => navigate('/platform/my-hosts')}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1,
                    bgcolor: isActive('/platform/my-hosts') ? 'secondary.main' : 'transparent',
                    color: isActive('/platform/my-hosts') ? 'white' : 'inherit',
                    border: '2px solid',
                    borderColor: isActive('/platform/my-hosts') ? 'primary.main' : 'inherit',
                    '&:hover': {
                      bgcolor: isActive('/platform/my-hosts') ? 'secondary.dark' : 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  Hostings
                </Button>
              )}

              <Button
                variant={isActive('/platform/bookings') ? 'contained' : 'outlined'}
                color={isActive('/platform/bookings') ? 'secondary' : 'inherit'}
                onClick={() => navigate('/platform/bookings')}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  bgcolor: isActive('/platform/bookings') ? 'secondary.main' : 'transparent',
                  color: isActive('/platform/bookings') ? 'white' : 'inherit',
                  border: '2px solid',
                  borderColor: isActive('/platform/bookings') ? 'primary.main' : 'inherit',
                  '&:hover': {
                    bgcolor: isActive('/platform/bookings') ? 'secondary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              >
               Appointments
              </Button>

            </Box>
          </Toolbar>
        </Box>
      )}
      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2rem', md: '2.5rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 5,
          // mb:3,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
        }}
        gutterBottom
      >
        Your Booked Sessions
      </Typography>
      <NeonSpeedometer value={courses.length} />

      <Box sx={{ mt: 2, px: { xs: 2, sm: 4, md: 8 }, ml: { sm: '40px' }, }}>





        {/* Status Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={status}
            onChange={(e, newValue) => {
              setStatus(newValue);
              setPage(1);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              my: 3,
              maxWidth: '100%',
            }}
          >
            <Tab label="Upcoming" value="Upcoming" />
            <Tab label="Live" value="Live" />
            <Tab label="Completed" value="Completed" />
          </Tabs>
        </Box>


        {/* No Sessions */}
        {!loading && courses.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6, minHeight: '50vh' }}>
            <EventBusyIcon sx={{ fontSize: 50 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              No {status} sessions found.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Session Cards */}
<Box
  sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: { xs: 2, sm: 3, md: 4 },
    mt: 1,
  }}
>
  {courses.map((course) => {
    const statusColorMap = {
      Upcoming: '#2196f3',
      Live: '#4caf50',
      Completed: '#b2b41cff',
    };
    const statusColor = statusColorMap[course.status] || '#888';

    return (
      <Box
        key={course.id}
        sx={{
          width: {
            xs: '100%',
            sm: 'calc(50% - 12px)',
            md: 'calc(50% - 16px)',
          },
          display: 'flex',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: 3,
            backgroundColor: '#000000ff',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: 3,
            transition: '0.3s',
            '&:hover': {
              boxShadow: 6,
              backgroundColor: '#111111',
            },
          }}
        >
          {/* Share Button */}
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <ShareButton
              postUrl={`${window.location.origin}/share/${
                course.is_bundle ? 'bundle' : 'post'
              }/${course.id}`}
            />
          </Box>

          <Link
            to={
              course.is_bundle
                ? `/my-bundle-booking-detail/${course.id}`
                : `/my-booking-tab-details/${course.id}`
            }
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <CardContent>
              {/* Title */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 500,
                  mr: 5,
                  fontSize: '1.1rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PlayCircleOutlineIcon sx={{ mr: 1 }} />
                {course.topic_name}
              </Typography>

              {/* Date / Time */}
              {course.selectedDate &&
                course.startTime &&
                course.duration && (
                  <Box sx={{ mt: 1, mb: 5 }}>
                    <Typography
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#ffffff',
                        background:
                          'linear-gradient(135deg, #2079c2ff 0%, #1f2c33ff 100%)',
                        px: 2,
                        py: 0.5,
                        borderRadius: '18px',
                        boxShadow: '0 2px 10px rgba(48, 71, 83, 0.4)',
                        whiteSpace: 'nowrap',
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                        letterSpacing: 0.4,
                        userSelect: 'none',
                        width: 'fit-content',
                        cursor: 'default',
                      }}
                    >
                      🗓
                      <span>
                        {formatTimeToAMPM(course.startTime)} •{' '}
                        {formatDateWithSuffix(course.selectedDate)} •{' '}
                        {formatDuration(course.duration)}
                      </span>
                    </Typography>
                  </Box>
                )}

              {/* Status Badge */}
              <Box
                sx={{
                  mt: 2,
                  display: 'inline-block',
                  backgroundColor: statusColor,
                  color: '#fff',
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {course.status}
              </Box>
            </CardContent>
          </Link>

          {/* Buttons */}
          <Box px={2} pb={2}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#2196f3',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  },
                }}
                onClick={() =>
                  navigate(
                    `/my-booking-tab-details/${course.id}/${course.booking_id}`
                  )
                }
              >
                View Session
              </Button>
              <Button
  variant="outlined"
  fullWidth
  disabled={course.status !== 'Live'}
  sx={{
    borderColor: '#2196f3',
    color: course.status !== 'Live' ? '#aaa' : '#2196f3',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor:
        course.status !== 'Live'
          ? 'inherit'
          : 'rgba(33,150,243,0.1)',
      borderColor:
        course.status !== 'Live'
          ? '#aaa'
          : '#2196f3',
    },
  }}
  onClick={() => {
    if (course.status === 'Live') {
      // navigate(`/meeting/${course.id}`);
      navigate(
  `/prejoin-1v1?bookingId=${course.booking_id}&expert_name=${course.expert_name}`
);
    }
  }}
>
  Join Now
</Button>

              {/* <Button
                variant="outlined"
                fullWidth
                disabled={
                  course.status === 'Completed' 
                }
                sx={{
                  borderColor: '#2196f3',
                  color:
                    course.status === 'Completed'
                      ? '#aaa'
                      : '#2196f3',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor:
                      course.status === 'Completed'
                        ? 'inherit'
                        : 'rgba(33,150,243,0.1)',
                    borderColor:
                      course.status === 'Completed'
                        ? '#aaa'
                        : '#2196f3',
                  },
                }}
                onClick={() => {
  if (course.status !== 'Completed') {
    navigate(`/meeting/${course.id}`);
  }
}}
              >
                Join Now
              </Button> */}
            </Box>
          </Box>
        </Card>
      </Box>
    );
  })}
</Box>


            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 6 }}>
                <Button
                  variant="outlined"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Typography variant="body1" sx={{ alignSelf: 'center' }}>
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>


      <Footer2 />
    </Box>
  );
}

export default MyBookings;
